import React, { useContext, useLayoutEffect, useRef, useState } from "react";
import {
  Button as AntdButton,
  Col,
  Row,
  ButtonProps as AntdButtonProps,
  message,
  Select,
  Spin,
  Table,
  Descriptions,
  Tooltip,
  DatePicker,
  InputNumber,
  Input,
  Popconfirm,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { RiFileEditLine } from "react-icons/ri";
import { useModal } from "react-modal-hook";
import { useTranslation } from "react-i18next";
import ProductContext from "../../../../context/product/context";
import FormHookModal from "../../modals/form-hook-modal";
import FormItem from "../../form-item";
import EditablePickSequence from "../../table-components/editable-pick-sequence";
import { IQuantity } from "../../../../models/product/response";
import EndPoints from "../../../../services/end-points";
import { IBatch } from "../../../../models/wms/batch/response";
import { IUpdateBatch } from "../../../../models/wms/batch/request";
import dayjs from "dayjs";

export interface ButtonProps extends AntdButtonProps {
  passedId: number;
  quantities: IQuantity[];
  productName?: string;
}

// Helper function to get display name from store_name
const getStoreName = (name: string | { ar: string; en: string }): string => {
  if (typeof name === "string") return name;
  return name?.ar || name?.en || "-";
};

const formatStorableLocation = (storable: IBatch["storable"]): string => {
  if (!storable) return "-";
  const parts = [
    storable.zone?.name,
    storable.aisle?.name,
    storable.storageUnit?.name,
    storable.shelf?.name,
    storable.name,
  ].filter(Boolean) as string[];

  // If backend doesn't send the hierarchy, fallback to the storable name.
  if (parts.length === 0) return storable.name ?? "-";
  return parts.join(" / ");
};

/** The stored cost as text, so an untouched empty cell stays empty instead of
 *  turning a missing price into a zero. */
const purchasePriceToInput = (price: number | null | undefined): string =>
  price === null || price === undefined ? "" : String(price);

interface IBatchRow extends IBatch {
  editedEntered: number;
  editedBatchNumber: string;
  editedNotes: string;
  editedExpirationDate: string;
  editedPurchasePrice: string;
  dirty: boolean;
  saving: boolean;
  deleting: boolean;
}

const MatchQuantityButton: React.FC<ButtonProps> = ({
  passedId,
  quantities,
  productName,
  ...props
}) => {
  const { t } = useTranslation();
  const { actions, loading } = useContext(ProductContext);

  const [showFormModal, hideFormModal] = useModal(() => {
    const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
    const [loadingBatches, setLoadingBatches] = useState(false);
    const [rows, setRows] = useState<IBatchRow[]>([]);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const tableWrapRef = useRef<HTMLDivElement>(null);
    const [tableScrollY, setTableScrollY] = useState(280);

    const storeQuantity = quantities.find(
      (q) => q.store_id === selectedStoreId,
    );

    useLayoutEffect(() => {
      const el = tableWrapRef.current;
      if (!el) return;
      const measure = () => {
        const h = el.clientHeight;
        setTableScrollY(Math.max(120, Math.floor(h - 112)));
      };
      measure();
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }, [selectedStoreId, loadingBatches, rows.length]);

    const loadBatches = async (opts?: {
      storeId?: number;
      page?: number;
      perPage?: number;
    }) => {
      const storeId = opts?.storeId ?? selectedStoreId;
      const nextPage = opts?.page ?? page;
      const nextPerPage = opts?.perPage ?? perPage;
      if (!storeId) return;

      setLoadingBatches(true);
      try {
        const { data } = await EndPoints.wmsBatch.getAllBatches({
          productId: passedId,
          storeId,
          page: nextPage,
          perPage: nextPerPage,
        });
        const batches: IBatch[] = data?.data ?? [];
        setTotal(data?.total ?? 0);
        setPage(data?.currentPage ?? nextPage);
        setRows(
          batches.map((b) => ({
            ...b,
            editedEntered: b.enteredQuantity,
            editedBatchNumber: b.batchNumber ?? "",
            editedNotes: b.notes ?? "",
            editedExpirationDate: b.expirationDate ?? "",
            editedPurchasePrice: purchasePriceToInput(b.purchasePrice),
            dirty: false,
            saving: false,
            deleting: false,
          })),
        );
      } catch (error: any) {
      } finally {
        setLoadingBatches(false);
      }
    };

    const handleStoreChange = async (storeId: number) => {
      setSelectedStoreId(storeId);
      setRows([]);
      setTotal(0);
      setPage(1);
      if (!storeId) return;
      await loadBatches({ storeId, page: 1 });
    };

    const updateRow = (
      id: number,
      field:
        | "editedEntered"
        | "editedExpirationDate"
        | "editedBatchNumber"
        | "editedNotes"
        | "editedPurchasePrice",
      value: string,
    ) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.id !== id) return r;
          const isNumberField = field === "editedEntered";
          const next = { ...r, [field]: isNumberField ? Number(value) : value };
          next.dirty =
            next.editedEntered !== r.enteredQuantity ||
            next.editedExpirationDate !== (r.expirationDate ?? "") ||
            next.editedBatchNumber !== (r.batchNumber ?? "") ||
            next.editedNotes !== (r.notes ?? "") ||
            next.editedPurchasePrice !== purchasePriceToInput(r.purchasePrice);
          return next;
        }),
      );
    };

    const saveBatch = async (row: IBatchRow) => {
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, saving: true } : r)),
      );
      const editedPrice = row.editedPurchasePrice.trim();
      const priceChanged =
        editedPrice !== purchasePriceToInput(row.purchasePrice);

      // A price can be corrected but never unset: the report reads the cost of
      // every batch it prices, and the server has no way to store "unknown".
      if (priceChanged && (editedPrice === "" || Number(editedPrice) < 0)) {
        message.error(t("batch_purchase_price_required"));
        setRows((prev) =>
          prev.map((r) => (r.id === row.id ? { ...r, saving: false } : r)),
        );
        return;
      }

      const payload: IUpdateBatch = {
        enteredQuantity: row.editedEntered,
        batchNumber: row.editedBatchNumber || undefined,
        notes: row.editedNotes || undefined,
        expirationDate: row.editedExpirationDate || undefined,
      };

      // Only a retyped cost is sent: an untouched field must leave the stored
      // price alone, and the stamped currency is never editable at all.
      if (priceChanged) payload.purchasePrice = Number(editedPrice);

      try {
        await EndPoints.wmsBatch.updateBatch(row.id, payload);
        setRows((prev) =>
          prev.map((r) =>
            r.id === row.id
              ? {
                  ...r,
                  enteredQuantity: row.editedEntered,
                  batchNumber: row.editedBatchNumber || undefined,
                  notes: row.editedNotes || undefined,
                  availableQuantity: row.editedEntered + r.withdrawnQuantity,
                  expirationDate: row.editedExpirationDate || undefined,
                  purchasePrice: priceChanged
                    ? Number(editedPrice)
                    : r.purchasePrice,
                  dirty: false,
                  saving: false,
                }
              : r,
          ),
        );
        actions.getData();
      } catch (error: any) {
        setRows((prev) =>
          prev.map((r) => (r.id === row.id ? { ...r, saving: false } : r)),
        );
      }
    };

    const deleteBatch = async (row: IBatchRow) => {
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, deleting: true } : r)),
      );
      try {
        await EndPoints.wmsBatch.deleteBatch(row.id);
        message.success(t("batch_deleted_successfully"));
        await loadBatches();
        actions.getData();
      } catch (error: any) {
        setRows((prev) =>
          prev.map((r) => (r.id === row.id ? { ...r, deleting: false } : r)),
        );
      }
    };

    const columns = [
      {
        title: t("id"),
        dataIndex: "id",
        key: "id",
        width: 72,
        align: "center" as const,
      },
      {
        title: t("current_location"),
        key: "location",
        width: 220,
        align: "center" as const,
        onCell: () => ({
          style: { overflow: "hidden", maxWidth: 220, verticalAlign: "middle" },
        }),
        render: (_: any, record: IBatchRow) => {
          const text = formatStorableLocation(record.storable);
          return (
            <Tooltip title={text}>
              <div
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  margin: "0 auto",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  textAlign: "center",
                  boxSizing: "border-box",
                }}
              >
                {text}
              </div>
            </Tooltip>
          );
        },
      },
      {
        title: t("batch_number"),
        key: "batchNumber",
        width: 180,
        align: "center" as const,
        render: (_: any, record: IBatchRow) => (
          <Input
            size="small"
            value={record.editedBatchNumber}
            onChange={(e) =>
              updateRow(record.id, "editedBatchNumber", e.target.value)
            }
          />
        ),
      },
      {
        title: t("expiration_date"),
        key: "expirationDate",
        width: 148,
        align: "center" as const,
        render: (_: any, record: IBatchRow) => (
          <DatePicker
            size="small"
            value={
              record.editedExpirationDate
                ? dayjs(record.editedExpirationDate)
                : null
            }
            onChange={(_date, dateString) =>
              updateRow(record.id, "editedExpirationDate", dateString as string)
            }
          />
        ),
      },
      {
        title: t("batch_purchase_price_selling_unit"),
        key: "purchasePrice",
        width: 180,
        align: "center" as const,
        render: (_: any, record: IBatchRow) => (
          <Tooltip title={t("batch_purchase_price_currency_locked")}>
            <Input
              size="small"
              type="number"
              min={0}
              value={record.editedPurchasePrice}
              addonAfter={record.purchasePriceIsDollar ? "$" : t("currency")}
              placeholder={t("batch_purchase_price")}
              onChange={(e) =>
                updateRow(record.id, "editedPurchasePrice", e.target.value)
              }
            />
          </Tooltip>
        ),
      },
      {
        title: t("note"),
        key: "notes",
        width: 180,
        align: "center" as const,
        render: (_: any, record: IBatchRow) => (
          <Input
            size="small"
            value={record.editedNotes}
            onChange={(e) =>
              updateRow(record.id, "editedNotes", e.target.value)
            }
          />
        ),
      },
      {
        title: t("entered_quantity"),
        key: "enteredQuantity",
        width: 112,
        align: "center" as const,
        render: (_: any, record: IBatchRow) => (
          <InputNumber
            size="small"
            min={0}
            style={{ width: 80 }}
            value={record.editedEntered}
            onChange={(val) =>
              updateRow(record.id, "editedEntered", String(val ?? 0))
            }
          />
        ),
      },
      {
        title: t("withdrawn_quantity"),
        dataIndex: "withdrawnQuantity",
        key: "withdrawnQuantity",
        width: 112,
        align: "center" as const,
        render: (val: number) => Math.abs(val ?? 0),
      },
      {
        title: t("available"),
        key: "available",
        width: 96,
        align: "center" as const,
        render: (_: any, record: IBatchRow) =>
          Math.max(0, record.editedEntered + (record.withdrawnQuantity ?? 0)),
      },
      {
        title: t("actions"),
        key: "action",
        width: 170,
        align: "center" as const,
        render: (_: any, record: IBatchRow) => (
          <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
            <AntdButton
              size="small"
              type="primary"
              loading={record.saving}
              disabled={!record.dirty || record.deleting}
              onClick={() => saveBatch(record)}
            >
              {t("save")}
            </AntdButton>
            <Popconfirm
              title={t("confirm_delete_batch")}
              okText={t("delete")}
              cancelText={t("cancel")}
              onConfirm={() => deleteBatch(record)}
              disabled={
                record.deleting || Math.abs(record.withdrawnQuantity ?? 0) > 0
              }
            >
              <AntdButton
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={record.deleting}
                disabled={
                  record.deleting || Math.abs(record.withdrawnQuantity ?? 0) > 0
                }
              />
            </Popconfirm>
          </div>
        ),
      },
    ];

    return (
      <FormHookModal
        id="match-quantity-modal"
        confirmButtonText={t("done")}
        loading={loading.includes("matchQuantity")}
        title={t("match_quantity")}
        subtitle={
          productName ? `${t("product_name")}: ${productName}` : undefined
        }
        hideModal={hideFormModal}
        width="86vw"
        bodyScroll
        hideFooter
        style={{ marginTop: "1.6rem", maxHeight: "85vh" }}
        onSubmit={() => {}}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            width: "100%",
            minWidth: 0,
            height: "calc(85vh - 180px)",
            overflowY: "auto",
            overflowX: "hidden",
            paddingRight: 4,
          }}
        >
          <div style={{ flexShrink: 0 }}>
            <FormItem label={t("store")}>
              <Select
                style={{ width: "100%" }}
                value={selectedStoreId}
                onChange={handleStoreChange}
                options={quantities.map((q) => ({
                  value: q.store_id,
                  label: getStoreName(q.store_name),
                }))}
              />
            </FormItem>
          </div>

          {storeQuantity && (
            <div style={{ flexShrink: 0 }}>
              <Descriptions size="small" bordered column={3}>
                <Descriptions.Item label={t("entered_quantity")}>
                  {storeQuantity.entered_quantity}
                </Descriptions.Item>
                <Descriptions.Item label={t("withdrawn_quantity")}>
                  {Math.abs(storeQuantity.withdrawn_quantity)}
                </Descriptions.Item>
                <Descriptions.Item label={t("available")}>
                  {storeQuantity.quantity}
                </Descriptions.Item>
              </Descriptions>
            </div>
          )}

          {selectedStoreId &&
            (loadingBatches ? (
              <Row justify="center" style={{ flexShrink: 0 }}>
                <Spin />
              </Row>
            ) : (
              <div
                ref={tableWrapRef}
                style={{ flex: 1, minHeight: 0, overflow: "hidden" }}
              >
                <Table
                  rowKey="id"
                  size="small"
                  dataSource={rows}
                  columns={columns}
                  pagination={{
                    current: page,
                    pageSize: perPage,
                    total,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showLessItems: true,
                    showTotal: (tot, range) =>
                      `${range[0]}-${range[1]} / ${tot}`,
                    onChange: (nextPage, nextPageSize) => {
                      const normalizedPageSize = nextPageSize ?? perPage;
                      setPerPage(normalizedPageSize);
                      setPage(nextPage);
                      loadBatches({
                        page: nextPage,
                        perPage: normalizedPageSize,
                      });
                    },
                  }}
                  scroll={{ x: 1280, y: Math.max(180, tableScrollY) }}
                  locale={{ emptyText: t("no_batches") }}
                />
              </div>
            ))}
        </div>
      </FormHookModal>
    );
  }, [passedId, quantities, productName, loading]);

  return (
    <AntdButton
      type="primary"
      icon={<RiFileEditLine />}
      onClick={showFormModal}
      {...props}
    />
  );
};

export default MatchQuantityButton;
